(function () {
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const ORDER_RE = /^#?[A-Za-z0-9-]{3,20}$/;

  // Phone: ≥9 and ≤15 digits after stripping non-digits. Worker normalises
  // to last-9 for matching against billingAddress.phone, so any local format
  // (+60, 60, 011-…) works as long as the digit count is sane.
  function isPhoneLike(s) {
    const digits = String(s || '').replace(/\D/g, '');
    return digits.length >= 9 && digits.length <= 15;
  }

  const ICONS = {
    not_found: '<circle cx="12" cy="12" r="10"/><line x1="9" y1="9" x2="15" y2="15"/><line x1="15" y1="9" x2="9" y2="15"/>',
    ok: '<path d="M20 6 9 17l-5-5"/>',
    cancelled: '<circle cx="12" cy="12" r="10"/><path d="M4.93 4.93l14.14 14.14"/>',
  };

  function init(root) {
    if (!root || root.__trackOrderInit) return;
    root.__trackOrderInit = true;

    const form = root.querySelector('[data-track-order-form]');
    if (!form) return;
    const status = root.querySelector('[data-track-order-status]');
    const timelines = Array.from(root.querySelectorAll('[data-track-order-timeline]'));
    const result = root.querySelector('[data-track-order-result]');
    const resultIcon = result && result.querySelector('.track-order__result-icon');
    const resultTitle = root.querySelector('[data-track-order-result-title]');
    const resultMessage = root.querySelector('[data-track-order-result-message]');
    const endpoint = root.dataset.trackEndpoint || '';

    form.querySelectorAll('.field__input').forEach((input) => {
      input.addEventListener('input', () => clearFieldError(input.name));
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      clearAllErrors();
      hideResult();

      const contact = form.email.value.trim();
      const num = form.order_number.value.trim();

      let valid = true;
      if (!contact) {
        setFieldError('email', 'Enter your email or phone number.');
        valid = false;
      } else if (!EMAIL_RE.test(contact) && !isPhoneLike(contact)) {
        setFieldError('email', "Enter a valid email or phone number.");
        valid = false;
      }
      if (!num) {
        setFieldError('order_number', 'Enter your order number.');
        valid = false;
      } else if (!ORDER_RE.test(num)) {
        setFieldError('order_number', 'Order number should be 3-20 letters or digits (e.g. 1042).');
        valid = false;
      }
      if (!valid) {
        const firstInvalid = form.querySelector('[aria-invalid="true"]');
        if (firstInvalid) firstInvalid.focus();
        return;
      }

      if (!endpoint) {
        showStatus('Tracking is temporarily unavailable. Please contact support.', 'error');
        return;
      }

      showStatus('Looking up your order…', 'info');
      const submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;
      timelines.forEach((tl) => { tl.dataset.state = 'loading'; });

      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contact, order_number: num }),
        });
        const data = await res.json().catch(() => ({}));

        if (res.status === 429) {
          showStatus(data.message || 'Too many requests. Please wait a minute.', 'error');
          return;
        }
        if (!res.ok || data.status === 'not_found') {
          paintFlow('delivery', 0);
          showResult({
            kind: 'not_found',
            title: 'No matching order',
            message: "We couldn't find an order with that email and order number. Double-check your confirmation email, or contact us if you need help.",
          });
          showStatus('', null);
          return;
        }
        if (data.status !== 'ok') {
          showStatus(data.message || 'Could not look up your order. Please try again.', 'error');
          return;
        }

        const flow = data.flow === 'pickup' ? 'pickup' : 'delivery';
        const stage = parseInt(data.stage_index, 10) || 1;
        const tl = paintFlow(flow, stage);
        const stageLabel = tl ? labelForStage(tl, stage) : '';

        if (data.cancelled) {
          showResult({
            kind: 'cancelled',
            title: 'Order ' + data.order_name + ' was cancelled',
            message: 'If this is unexpected, please contact us.',
          });
          showStatus('', null);
        } else {
          const when = data.updated_at ? ' · updated ' + formatDate(data.updated_at) : '';
          showResult({
            kind: 'ok',
            title: 'Order ' + data.order_name,
            message: stageLabel + when,
          });
          showStatus('', null);
        }
      } catch (err) {
        showStatus('Network error. Please try again.', 'error');
      } finally {
        if (submitBtn) submitBtn.disabled = false;
        timelines.forEach((tl) => { delete tl.dataset.state; });
      }
    });

    function setFieldError(name, message) {
      const errEl = form.querySelector('[data-error-for="' + name + '"]');
      const input = form.querySelector('[name="' + name + '"]');
      if (errEl) { errEl.hidden = false; errEl.textContent = message; }
      if (input) input.setAttribute('aria-invalid', 'true');
    }

    function clearFieldError(name) {
      const errEl = form.querySelector('[data-error-for="' + name + '"]');
      const input = form.querySelector('[name="' + name + '"]');
      if (errEl) { errEl.hidden = true; errEl.textContent = ''; }
      if (input) input.removeAttribute('aria-invalid');
    }

    function clearAllErrors() {
      form.querySelectorAll('[data-error-for]').forEach((el) => { el.hidden = true; el.textContent = ''; });
      form.querySelectorAll('.field__input[aria-invalid="true"]').forEach((el) => el.removeAttribute('aria-invalid'));
    }

    function showResult({ kind, title, message }) {
      if (!result) return;
      result.hidden = false;
      result.dataset.result = kind;
      if (resultIcon) resultIcon.innerHTML = ICONS[kind] || '';
      if (resultTitle) resultTitle.textContent = title || '';
      if (resultMessage) resultMessage.textContent = message || '';
    }

    function hideResult() {
      if (!result) return;
      result.hidden = true;
      delete result.dataset.result;
      if (resultIcon) resultIcon.innerHTML = '';
      if (resultTitle) resultTitle.textContent = '';
      if (resultMessage) resultMessage.textContent = '';
    }

    function showStatus(msg, state) {
      if (!status) return;
      if (!msg) { status.hidden = true; status.textContent = ''; status.dataset.state = ''; return; }
      status.hidden = false;
      status.textContent = msg;
      status.dataset.state = state || '';
    }

    function paintFlow(flow, activeIndex) {
      let visible = null;
      timelines.forEach((tl) => {
        if (tl.dataset.flow === flow) {
          tl.hidden = false;
          paintTimeline(tl, activeIndex);
          visible = tl;
        } else {
          tl.hidden = true;
        }
      });
      return visible;
    }

    function paintTimeline(tl, activeIndex) {
      tl.querySelectorAll('[data-stage-index]').forEach((el) => {
        const idx = parseInt(el.dataset.stageIndex, 10);
        const oneBased = idx + 1;
        el.classList.remove('track-order__stage--upcoming', 'track-order__stage--completed', 'track-order__stage--active');
        if (oneBased < activeIndex) el.classList.add('track-order__stage--completed');
        else if (oneBased === activeIndex) el.classList.add('track-order__stage--active');
        else el.classList.add('track-order__stage--upcoming');
      });
    }

    function labelForStage(tl, oneBasedIndex) {
      const li = tl.querySelector('[data-stage-index="' + (oneBasedIndex - 1) + '"]');
      return li ? (li.dataset.stageLabel || '') : '';
    }

    function formatDate(iso) {
      try {
        return new Date(iso).toLocaleString('en-MY', {
          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
        });
      } catch (e) {
        return iso;
      }
    }
  }

  function bootAll() {
    document.querySelectorAll('.track-order').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  // Re-init when Shopify re-renders the section in the theme editor
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector('.track-order');
    if (root) init(root);
  });
})();
